<script lang="ts" setup>import { ref as _ref, toRef as _toRef } from 'vue';

import type { Attachment } from '@consumer/types/serializers'
import { faPause, faPlay } from '@fortawesome/pro-solid-svg-icons'
import GIconButton from '@consumer/components/GIconButton.vue'
import { useScreen } from '@consumer/helpers/screen'
import Waveform, { WAVEFORM_MAX_WIDTH_PX } from './Waveform.vue'

withDefaults(defineProps<{
  voiceMemo: Attachment | string | null | undefined
  // True when this page is the one the recipient is currently viewing
  // in ReadingStage's page strip. When it flips to false (they swipe
  // away to another page) we pause playback so audio doesn't keep
  // running unseen. Default `true` so the component still works when
  // rendered standalone outside the page strip.
  active?: boolean
}>(), { active: true, })

// Imperative handle into the Waveform child. `instanceof typeof` keeps
// TS happy with the exposed methods (`play`, `pause`, `setTime`,
// `getCurrentTime`). The child never re-renders during playback —
// updates to `currentTime` below stay scoped to this component.
const waveformRef = ref<InstanceType<typeof Waveform>>()
const elapsedTimeRef = _ref<HTMLElement>()
const __$temp_1 = (useScreen()),
  isMobile = _toRef(__$temp_1, 'isMobile');

const isPlaying = ref(false)
const playedOnce = ref(false)
const duration = ref(0)

// WaveSurfer's `timeupdate` fires every ~250ms (it mirrors the
// underlying `<audio>` element's cadence), which makes the
// full-height progress line read as visibly stepping every quarter
// second. While the audio is playing we poll the child's
// `getCurrentTime()` on every animation frame instead so `currentTime`
// (and the dependent `progressLineLeft` computed) advances smoothly.
// The loop only runs between `play` and `pause`/`finish` so we're not
// burning CPU when nothing's moving.
let rafId: number | null = null

function tickProgress () {
  if (waveformRef.value) {
    const t = waveformRef.value.getCurrentTime()
    // iOS Safari quirk: between `media.pause()` being called and the
    // media element firing its `pause` event (which is what stops this
    // loop via `onPause`), `media.currentTime` can momentarily report
    // 0 even though playback position hasn't actually moved. Reject 0
    // here — explicit "back to start" transitions come through
    // `onFinish` or `onSeeking` instead.
    if (t > 0) setCurrentTime(t)
  }
  rafId = requestAnimationFrame(tickProgress)
}

function startProgressLoop () {
  if (rafId !== null) return
  rafId = requestAnimationFrame(tickProgress)
}

function stopProgressLoop () {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

// `WAVEFORM_MAX_WIDTH_PX` is imported from Waveform.vue so the line +
// pointer math here automatically follows any change to the canvas's
// max-width. PAGE_PADDING_X_PX is local — it matches the `.page-voice-memo`
// horizontal padding below.
const PAGE_PADDING_X_PX = 20

function setProgressLineLeft (time: number) {
  const dur = duration.value
  let left: string
  if (!dur) {
    left = '2px'
  }
  else {
    // Waveform width = `min(parent - 2*padding, max-width)`; it's
    // horizontally centered, so the line starts at
    // `(parent - waveformWidth) / 2`, NOT at the page's padding edge.
    // On narrow viewports this resolves to the original 20px offset;
    // on wide viewports it grows so the line stays aligned with the
    // centered waveform.
    const fraction = time / dur
    const ww = `min(100% - ${PAGE_PADDING_X_PX * 2}px, ${WAVEFORM_MAX_WIDTH_PX}px)`
    left = `calc((100% - ${ww}) / 2 + ${ww} * ${fraction})`
  }
  progressLineEl.value?.style.setProperty('left', left)
}

function setElapsedTime (time: number) {
  const elapsedHours = Math.floor(time / 3600)
  const elapsedMinutes = Math.floor((time % 3600) / 60)
  const elapsedSeconds = Math.floor(time % 60)
  if (elapsedTimeRef.value)
    elapsedTimeRef.value.textContent = [
      elapsedHours.toString().padStart(2, '0'),
      elapsedMinutes.toString().padStart(2, '0'),
      elapsedSeconds.toString().padStart(2, '0'),
    ].join(':')
}

// ===== Waveform event handlers =====

function onReady (d: number) {
  duration.value = d
}

function onSeeking (newTime: number) {
  setCurrentTime(newTime)
}

function onDrag (relativeX: number) {
  if (!duration.value) return
  setCurrentTime(relativeX * duration.value)
}

function onFinish () {
  isPlaying.value = false
  setCurrentTime(0)
  stopProgressLoop()
  // Rewind WaveSurfer's own playback position too — without this its
  // internal time stays at duration, so the played/unplayed split in
  // the waveform canvas still reads as "fully played" even though our
  // progress line has snapped back to the start.
  waveformRef.value?.setTime(0)
}

function onPlay () {
  isPlaying.value = true
  playedOnce.value = true
  startProgressLoop()
}

function onPause () {
  isPlaying.value = false
  stopProgressLoop()
}

function togglePlay () {
  if (isPlaying.value) waveformRef.value?.pause()
  else waveformRef.value?.play()
}

function setCurrentTime (time: number) {
  setProgressLineLeft(time)
  setElapsedTime(time)
}

// ===== Drag-to-seek on the progress line itself =====
//
// The line's hit area is 30px wide (see CSS `width: 30px` on
// `.voice-memo-progress-line` — the visible dashes stay 3px and are
// re-centered via `mask-position`). When the recipient grabs the line,
// we resolve the pointer x into a time relative to the page's
// horizontal padding (20px each side, matching where the waveform
// itself sits) and push that into both the Waveform child's `setTime`
// and our local `currentTime` ref. The child will fire `seeking` in
// response (which also writes `currentTime`), but a same-value write
// is a no-op so the double assignment is harmless.
const progressLineEl = ref<HTMLDivElement>()
let isDraggingLine = false

function setTimeFromPointer (e: PointerEvent) {
  if (!duration.value || !waveformRef.value) return
  const parent = progressLineEl.value?.parentElement
  if (!parent) return
  const rect = parent.getBoundingClientRect()
  // Match the centered, max-600 waveform's horizontal bounds — same
  // formula as `setProgressLineLeft`. Clamp the pointer x to that
  // span before converting to time.
  const waveformWidth = Math.min(rect.width - PAGE_PADDING_X_PX * 2, WAVEFORM_MAX_WIDTH_PX)
  if (waveformWidth <= 0) return
  const waveformLeftOffset = (rect.width - waveformWidth) / 2
  const xWithin = Math.max(0, Math.min(waveformWidth, e.clientX - rect.left - waveformLeftOffset))
  const newTime = (xWithin / waveformWidth) * duration.value
  waveformRef.value.setTime(newTime)
  setCurrentTime(newTime)
}

function onLinePointerDown (e: PointerEvent) {
  if (!duration.value) return
  // The user clicked the line element directly — claim the gesture
  // and stop the event from reaching WaveSurfer (which listens for
  // pointerdown at the document level) so it doesn't also start a
  // drag on the waveform underneath.
  e.stopPropagation()
  isDraggingLine = true
  ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  setTimeFromPointer(e)
}

function onLinePointerMove (e: PointerEvent) {
  // If the user is dragging the waveform (not the line), this handler
  // still fires whenever the progress line slides under the cursor
  // mid-drag. We must NOT stopPropagation in that case — WaveSurfer's
  // document-level pointermove listener needs to keep receiving the
  // event so its `drag` keeps firing. Only stop propagation when
  // we actually own the gesture.
  if (!isDraggingLine) return
  e.stopPropagation()
  setTimeFromPointer(e)
}

function onLinePointerUp (e: PointerEvent) {
  // Same reasoning as `onLinePointerMove`: if the waveform owns this
  // gesture, the line is just incidentally under the cursor. Letting
  // the event bubble to the document is what makes WaveSurfer's
  // `dragend` (and therefore our `endSeek` resume) fire.
  if (!isDraggingLine) return
  e.stopPropagation()
  isDraggingLine = false
  ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
}

watch(
  () => elapsedTimeRef.value,
  () => setElapsedTime(0),
)

// Pause when the page strip slides off this slide. The recipient
// can resume by tapping play again once they navigate back.
watch(
  () => __props.active,
  (isActive) => {
    if (!isActive && isPlaying.value) waveformRef.value?.pause()
  },
)

onBeforeUnmount(stopProgressLoop)
</script>

<template>
  <div class="page-voice-memo">
    <div ref="elapsedTimeRef" class="voice-memo-time"/>

    <div class="w-full relative">
      <Waveform
        ref="waveformRef"
        class="voice-memo-waveform"
        :voiceMemo="voiceMemo"
        @ready="onReady"
        @seeking="onSeeking"
        @drag="onDrag"
        @finish="onFinish"
        @play="onPlay"
        @pause="onPause"
      />
      <Transition name="fade-in-out">
        <span v-if="isMobile && playedOnce" class="sound-on-badge">
          If you hear no sound, check silent mode or volume.
        </span>
      </Transition>
    </div>

    <GIconButton
      class="voice-memo-play-btn"
      :aria-label="isPlaying ? 'Pause voice memo' : 'Play voice memo'"
      :icon="isPlaying ? faPause : faPlay"
      size="large"
      iconSize="xl"
      secondary
      @pointerdown.stop
      @click.stop="togglePlay"
    />

    <!--
      Vertical dashed playback line. Spans the full page height (top to
      bottom of the viewport) and rides horizontally along the waveform
      as playback advances. The waveform itself sits at the page's
      horizontal padding inset (20px each side), so the line travels
      from `20px` to `(100% - 20px)` of the page as progress goes 0→1.
      Behind the play button (z below it) so taps still hit the button.
    -->
    <div
      ref="progressLineEl"
      class="voice-memo-progress-line"
      @pointerdown="onLinePointerDown"
      @pointermove="onLinePointerMove"
      @pointerup="onLinePointerUp"
      @pointercancel="onLinePointerUp"
    />
  </div>
</template>

<style scoped lang="scss">
.page-voice-memo {
  align-items: center;

  // Use the card-image-sampled background tint (set on the reading-stage
  // root by useImagePixelColor); fall back to the default card color when
  // sampling isn't available.
  background: var(--page-bg-color, var(--card-color));
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-evenly;
  padding: 150px 20px 120px;

  // Positioned so the .voice-memo-progress-line absolute-positioned child
  // resolves its top/bottom/left against this page (full viewport size).
  position: relative;
  width: 100%;
}

.voice-memo-time {
  backdrop-filter: blur(5px);

  // Pill backdrop in the same hue as the text, softened heavily via
  // color-mix() so it reads as a tinted halo rather than a solid badge.
  background: rgb(70 70 70 / 30%);
  border-radius: 9999px;
  color: white;
  display: inline-block;
  font-family: Gilroy, sans-serif;
  font-size: 18px;

  // Pin digit widths to the same advance so the pill doesn't reflow
  // each time the seconds tick — the surrounding repaint was visible
  // on mobile as a brief progress-line stutter once per second.
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  letter-spacing: 2px;
  padding: 10px 15px;
  z-index: 100;
}

// Full-height playback indicator: a vertical column of small
// stadium/pill shapes (6×14 each, repeated every 26px). Built as a
// solid fill in --font-color that's masked through an SVG
// rect-with-rx pattern, so the dashes pick up the admin's theme color
// automatically. CSS gradients can't produce a fully rounded cap on
// both ends of a dash, so the mask approach is what gets us the
// capsule silhouette.
$dash-svg: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 10'><rect x='0' y='0' width='3' height='6' rx='1.5' fill='black'/></svg>";

.voice-memo-progress-line {
  background-color: var(--font-color, var(--text-dark));
  bottom: 0;

  // Grab-anywhere drag-to-seek: ew-resize cursor on desktop hints that
  // the line itself is interactive, and `touch-action: none` keeps the
  // mobile browser from hijacking horizontal drags for scroll/back-nav.
  cursor: ew-resize;

  // Initial position = waveform's left edge (fraction = 0 of the
  // same formula used by JS in `setProgressLineLeft`). Without this,
  // the line would sit at the page's left edge until the first JS
  // update fired, which on wide viewports (where the waveform is
  // centered with max-width 600px) is visibly off.
  left: calc((100% - min(100% - 40px, 600px)) / 2);
  mask-image: url("#{$dash-svg}");

  // Element is now 30px wide so the line is easy to grab, but the
  // dashes themselves are still 3px — `mask-position: center` keeps
  // them painted as a column in the middle of the (invisible) hit area.
  mask-position: center;
  mask-repeat: repeat-y;
  mask-size: 3px 8px;
  position: absolute;
  top: 0;
  touch-action: none;
  transform: translateX(-50%);
  width: 30px;

  // Hint to the browser to promote this element to its own composited
  // layer so per-frame `left` updates don't repaint the rest of the
  // page underneath — keeps the line smooth on mobile.
  will-change: left;
  z-index: 50;
}

.sound-on-badge {
  align-items: center;

  // Auto-dismiss the hint after the user has had a chance to read it.
  // `forwards` keeps the badge invisible at the end of the fade.
  animation: sound-on-badge-dismiss 0.5s ease-out 4s forwards;
  backdrop-filter: blur(5px);
  background: rgb(70 70 70 / 50%);
  border-radius: 60px;
  box-shadow: 0 4px 13.9px 0 rgb(0 0 0 / 10%);
  color: white;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  font-family: Gilroy, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  gap: 5px;
  height: 31px;
  justify-content: center;

  // Center both horizontally and vertically on the page.
  // `top: 50%` + `left: 50%` anchors the top-left corner at the center
  // of the parent; `translate(-50%, -50%)` then shifts the badge back
  // by half its own size in each axis, which works regardless of the
  // badge's content-driven dimensions.
  letter-spacing: 0%;
  line-height: 140%;
  min-height: fit-content;
  padding: 10px 15px 10px 20px;
  position: absolute;
  text-align: center;
  vertical-align: middle;
  z-index: 120;
}

@keyframes sound-on-badge-dismiss {
  to {
    opacity: 0;
  }
}

.voice-memo-play-btn {
  border-radius: 50%;
  height: 80px;
  width: 80px;
  z-index: 100;

  :deep(.fa-play) {
    margin-left: 4px; // optical centering for the non-monospaced play icon
  }
}
</style>
